import type {PropsWithChildren} from 'react'
import {memo, useCallback, useEffect, useRef, useState} from 'react'
import {Alert, Box, Button, CircularProgress, Stack, Typography} from '@mui/material'
import ChatMessageList from './components/ChatMessageList'
import ChatInput from './components/ChatInput'
import ChatDropZone from './components/ChatDropZone'
import {useCheckTenantOptInQuery, useCreateMessageMutation} from '@apis/aiCoreAPI.ts'
import {useActiveConversation} from './hooks/useActiveConversation'
import {OpenConversationContext} from './hooks/useOpenConversation'
import Header from './components/Header'
import ConversationSwitcher from './components/ConversationSwitcher'
import {ConversationStreamProvider} from './components/ConversationStreamProvider'
import {useCustomTranslation} from '@shared/hooks/useCustomTranslation.ts'
import CacheInvalidationListener from '@containers/Dashboards/AgentChat/components/CacheInvalidationListener.tsx'
import {ALPINIST_STORAGE_KEYS} from '@shared/hooks/useAlpinistUrlSync'
import {useCurrentUser} from '@hooks/useCurrentUser'
import {useServiceCapabilities} from '@hooks/useServiceCapabilities'
import MissingPermissions from '@shared/components/MissingPermissions'
import {useResizeObserver} from '@shared/hooks/useResizeObserver'
import {ConversationRunProvider} from './components/conversationRun/ConversationRunProvider'
import {mergeIntoDraft} from './components/conversationRun/withdrawnDraft'
import {usePageContextRegistry} from '@shared/alpinist/pageContext'
import ScreenCaptureResponder from './components/ScreenCaptureResponder'

const CHAT_MIN = 250
const HISTORY_WIDTH = 220

/** Small reusable wrapper for centered content with optional maxWidth */
const CenteredMessage = memo(({children, maxWidth}: PropsWithChildren<{maxWidth?: number}>) => (
  <Stack alignItems="center" justifyContent="center" sx={{flex: 1, p: 4}}>
    <Box sx={{maxWidth}}>{children}</Box>
  </Stack>
))
CenteredMessage.displayName = 'CenteredMessage'

type ChatAreaProps = {
  checkingOptIn: boolean
  optInError: boolean
  /** Re-run the access check, for the retry the error message offers. */
  retryOptIn: () => void
  optedIn: boolean | undefined
  activeId: string | null | undefined
  draft: string
  setDraft: (v: string) => void
  sending: boolean
  onSend: (text: string, attachmentIds?: string[]) => Promise<void>
  isMobile?: boolean
  /** Texts Stop withdrew before Alpinist read them, back into the draft. */
  onWithdrawnTexts: (texts: string[]) => void
}

type AgentChatProps = {
  fullWidth?: boolean
  onToggleFullWidth?: () => void
  isMobile?: boolean
  initialConversationId?: string | null
  onConversationChange?: (id: string | null) => void
  draft?: string
  onDraftChange?: (value: string) => void
  /**
   * Optional gate run before a send. Return false to abort it (e.g. the public demo
   * widget uses this to enforce a free-message allowance). Omitted = always send.
   */
  beforeSend?: (text: string) => boolean
  /** Public demo embed: hide conversation controls (history, new, rename, full-width). */
  minimalChrome?: boolean
  /** Public demo embed: always start a fresh isolated conversation (see useActiveConversation). */
  forceNewConversation?: boolean
}

/** Early-return gate for all chat states, no ternaries needed */
const ChatArea = ({
  checkingOptIn,
  optInError,
  retryOptIn,
  optedIn,
  activeId,
  draft,
  setDraft,
  sending,
  onSend,
  isMobile = false,
  onWithdrawnTexts,
}: ChatAreaProps) => {
  const [t] = useCustomTranslation('alpinist')

  if (checkingOptIn) {
    return (
      <CenteredMessage>
        <Stack alignItems="center">
          <CircularProgress size={24} />
          <Typography variant="body2" color="text.secondary" sx={{mt: 1.5}}>
            {t('chat_checking_access')}
          </Typography>
        </Stack>
      </CenteredMessage>
    )
  }

  if (optInError) {
    // The check runs once, on mount, so a single failed request — a backend
    // still starting, an expired token, a dropped connection — used to hold the
    // whole panel for the rest of the session. The message said "try again
    // later" and gave nothing to try with; reloading the page was the only way
    // out. This is that button.
    return (
      <CenteredMessage maxWidth={440}>
        <Alert
          severity="error"
          action={
            <Button color="inherit" size="small" onClick={retryOptIn}>
              {t('chat_access_retry')}
            </Button>
          }
        >
          {t('chat_access_error')}
        </Alert>
      </CenteredMessage>
    )
  }

  if (optedIn === false) {
    return (
      <CenteredMessage maxWidth={300}>
        <Alert severity="warning">
          <Typography variant="body2" gutterBottom>
            {t('chat_no_access_warning')}
          </Typography>
        </Alert>
      </CenteredMessage>
    )
  }

  if (!activeId) {
    return (
      <CenteredMessage>
        <Stack alignItems="center">
          <CircularProgress size={24} />
          <Typography variant="body2" color="text.secondary" sx={{mt: 1.5}}>
            {t('chat_preparing_conversation')}
          </Typography>
        </Stack>
      </CenteredMessage>
    )
  }

  // Happy path: chat is ready
  const chat = (
    <ChatDropZone>
      <Box sx={{flex: 1, minHeight: 0, overflowY: 'auto', px: 2, pt: 1, pb: 1}}>
        <ChatMessageList conversationId={activeId} onResend={onSend} sending={sending} />
      </Box>
      <Box sx={{flexShrink: 0}}>
        <ChatInput
          conversationId={activeId}
          value={draft}
          onChange={setDraft}
          onSend={onSend}
          disabled={sending}
          isMobile={isMobile}
        />
      </Box>
    </ChatDropZone>
  )

  return (
    <ConversationRunProvider conversationId={activeId} onWithdrawnTexts={onWithdrawnTexts}>
      {chat}
    </ConversationRunProvider>
  )
}

const AgentChat = ({
  fullWidth = false,
  onToggleFullWidth,
  isMobile = false,
  initialConversationId,
  onConversationChange,
  draft,
  onDraftChange,
  beforeSend,
  minimalChrome = false,
  forceNewConversation = false,
}: AgentChatProps) => {
  const [historyOpen, setHistoryOpen] = useState(() => {
    try {
      return sessionStorage.getItem(ALPINIST_STORAGE_KEYS.historyOpen) === 'true'
    } catch {
      return false
    }
  })
  const [localDrafts, setLocalDrafts] = useState<Record<string, string>>({})
  const [createMessage, {isLoading: sending}] = useCreateMessageMutation()
  // Alpinist read access is required to use the assistant. The entry points stay
  // visible; opening the view without read shows a missing-permissions message.
  const {ai} = useServiceCapabilities()
  // The sender's own zone, so Alpinist answers "yesterday's shift" on the
  // plant's clock rather than UTC (AB#5875).
  const {profileTimezone} = useCurrentUser()
  // Only the side panel sits next to a visible page; full width and /agent-chat
  // (mobile) cover it, so they send no page context and never capture (AB#6762).
  // Outside DashTemplate (the demo embed) there is no registry at all.
  const pageContextRegistry = usePageContextRegistry()
  const sidePanel = !fullWidth && !isMobile && pageContextRegistry !== null

  const {
    data: optedIn,
    isLoading: checkingOptIn,
    isError: optInError,
    refetch: retryOptIn,
  } = useCheckTenantOptInQuery(undefined, {skip: !ai.read})

  const [containerW, setContainerW] = useState(0)

  const enabled = optedIn === true
  const {conversations, isLoading, activeId, setActiveId, createNew, creating} =
    useActiveConversation({
      enabled,
      bootstrap: enabled,
      initialConversationId,
      forceNew: forceNewConversation,
    })
  const draftValue = draft ?? (activeId ? (localDrafts[activeId] ?? '') : '')
  // Read by socket handlers and by a send in flight, which outlive a render.
  const draftRef = useRef(draftValue)
  draftRef.current = draftValue
  const setDraftValue = onDraftChange
    ? onDraftChange
    : (value: string) => {
        if (!activeId) {
          return
        }

        setLocalDrafts((prev) => {
          if (!value) {
            if (!(activeId in prev)) {
              return prev
            }

            const next = {...prev}
            delete next[activeId]
            return next
          }

          if (prev[activeId] === value) {
            return prev
          }

          return {...prev, [activeId]: value}
        })
      }

  // Sync activeId changes to URL (only when we have an actual conversation)
  useEffect(() => {
    if (activeId) {
      onConversationChange?.(activeId)
    }
  }, [activeId, onConversationChange])

  // Measure container width
  const handleRowResize = useCallback((entry: ResizeObserverEntry): void => {
    setContainerW(entry.contentRect.width)
  }, [])
  const rowRef = useResizeObserver(handleRowResize)

  const canInline = containerW >= CHAT_MIN + HISTORY_WIDTH + 1
  const prevCanInlineRef = useRef(canInline)

  // Persist the drawer state on explicit user actions (sessionStorage, same scope
  // as the Alpinist panel open state). The responsive auto-close below is
  // layout-driven, so it does NOT persist — the preference is kept for when the
  // layout can inline the drawer again.
  const persistHistoryOpen = useCallback((open: boolean) => {
    try {
      if (open) {
        sessionStorage.setItem(ALPINIST_STORAGE_KEYS.historyOpen, 'true')
      } else {
        sessionStorage.removeItem(ALPINIST_STORAGE_KEYS.historyOpen)
      }
    } catch {
      // best-effort persistence
    }
  }, [])

  const toggleHistory = useCallback(() => {
    setHistoryOpen((v) => {
      const next = !v
      persistHistoryOpen(next)
      return next
    })
  }, [persistHistoryOpen])
  useEffect(() => {
    const wasInline = prevCanInlineRef.current
    if (wasInline && !canInline && historyOpen) {
      setHistoryOpen(false)
    }
    prevCanInlineRef.current = canInline
  }, [canInline, historyOpen])

  const handleHistoryClose = useCallback(() => {
    if (!canInline) {
      setHistoryOpen(false)
      persistHistoryOpen(false)
    }
  }, [canInline, persistHistoryOpen])

  const handleConversationSelect = useCallback(
    (id: string | null) => {
      setActiveId(id)
    },
    [setActiveId],
  )

  const handleNewConversation = useCallback(async () => {
    // One click, one conversation: on a slow connection the old one stays on
    // screen until the new one exists, which invited a second click.
    if (creating) return
    setDraftValue('')
    await createNew()
  }, [createNew, creating, setDraftValue])

  const handleSend = async (text: string, attachmentIds?: string[]) => {
    if (!activeId || (!text.trim() && !attachmentIds?.length)) {
      return
    }
    if (beforeSend && !beforeSend(text)) {
      return
    }
    const draftAtSend = draftRef.current
    try {
      await createMessage({
        conversation_id: activeId,
        text,
        attachment_ids: attachmentIds,
        timezone: profileTimezone,
        page_context: sidePanel ? pageContextRegistry?.read() : undefined,
      }).unwrap()
    } finally {
      // The composer is live during a run, and Stop can hand text back while
      // this send is in flight: clear only what was sent.
      if (draftRef.current === draftAtSend) {
        setDraftValue('')
      }
    }
  }

  // Stop withdrew messages Alpinist had not read yet: their texts go back into
  // the composer, ahead of whatever is being typed there (AB#6760).
  const takeBackWithdrawn = (texts: string[]): void => {
    const next = mergeIntoDraft(texts, draftRef.current)
    draftRef.current = next
    setDraftValue(next)
  }

  // derived flags used outside ChatArea (e.g., header / history panel)
  const noAccess = optInError || (!checkingOptIn && optedIn === false)
  const showChat = !checkingOptIn && !optInError && optedIn === true && !!activeId

  if (!ai.read) {
    return <MissingPermissions segment="Alpinist" />
  }

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        flex: 1,
        minHeight: 0,
        bgcolor: 'background.paper',
      }}
    >
      <ConversationStreamProvider conversationId={activeId ?? null}>
        <OpenConversationContext.Provider value={handleConversationSelect}>
          <CacheInvalidationListener />
          {sidePanel && activeId && <ScreenCaptureResponder conversationId={activeId} />}
          <Box sx={{flexShrink: 0}}>
            <Header
              onOpenHistory={toggleHistory}
              onNewConversation={handleNewConversation}
              creatingConversation={creating}
              activeId={activeId}
              conversations={conversations}
              historyOpen={historyOpen}
              disabled={noAccess || checkingOptIn}
              fullWidth={fullWidth}
              onToggleFullWidth={onToggleFullWidth}
              isMobile={isMobile}
              minimalChrome={minimalChrome}
            />
          </Box>

          {/* Main area */}
          <Box
            ref={rowRef}
            sx={{
              display: 'flex',
              flex: 1,
              minHeight: 0,
              minWidth: 0,
              position: 'relative',
              overflow: 'hidden',
            }}
          >
            {/* Chat column */}
            <Box
              sx={{
                flex: '1 1 auto',
                minWidth: CHAT_MIN,
                display: 'flex',
                flexDirection: 'column',
                minHeight: 0,
              }}
            >
              <ChatArea
                checkingOptIn={checkingOptIn}
                optInError={!!optInError}
                retryOptIn={() => void retryOptIn()}
                optedIn={optedIn}
                activeId={activeId}
                draft={draftValue}
                setDraft={setDraftValue}
                sending={sending}
                onSend={handleSend}
                isMobile={isMobile}
                onWithdrawnTexts={takeBackWithdrawn}
              />
            </Box>

            {/* History (only when chat is shown) */}
            {historyOpen && showChat && (
              <Box
                sx={(t) => ({
                  width: HISTORY_WIDTH,
                  borderLeft: `1px solid ${t.palette.divider}`,
                  bgcolor: 'background.paper',
                  display: 'flex',
                  flexDirection: 'column',
                  minHeight: 0,
                  ...(canInline
                    ? {flex: '0 0 auto'}
                    : {
                        position: 'absolute',
                        top: 0,
                        right: 0,
                        bottom: 0,
                        boxShadow: t.shadows[6],
                        zIndex: 10,
                      }),
                })}
              >
                <ConversationSwitcher
                  onClose={handleHistoryClose}
                  conversations={conversations}
                  activeId={activeId}
                  onSelect={handleConversationSelect}
                  loading={isLoading}
                />
              </Box>
            )}
          </Box>
        </OpenConversationContext.Provider>
      </ConversationStreamProvider>
    </Box>
  )
}

export default AgentChat
